// packages/api/src/PublicTicketService.ts
//
// PUBLIC TICKET client — the read (`publicTicketForm`) that returns everything the
// Buy-Tickets drawer needs, and the two writes: `publicTicketReserve` (hold the
// selected tickets) and `publicTicketPay` (start the Stripe card charge). Anonymous +
// published-only, mirroring eventsmagic-api's PublicTicketCore / TicketOrderCore rails.
//
// Runs through the injected client (see `configureApiService`) so it works in the
// BROWSER — the drawer reads on open and writes as the guest steps through checkout.
import { gql } from "@apollo/client";
import { OpResult, OP_RESULT_CODES } from "@sdflc/api-helpers";

import { ApiService } from "./ApiService";
import { refusalReason } from "./opResultHelpers";
import { PAY_LINK_REFUNDED_FIELD, QUESTION_IMAGE_FIELDS, executeQueryImageSafe, imageSafeDocs } from "./answerImageFields";
import type {
  PublicRsvpEventRef,
  PublicRsvpFormQuestion,
  PublicRsvpAnswerInput,
  PublicRsvpFormPolicy,
  PublicRsvpPolicyAcceptanceInput,
  PublicRsvpGuardianInput,
} from "./PublicRsvpService";
import type { PublicEventTheme } from "./PublicEventService";
import { PUBLIC_CAUSE_FIELD_LIST, type PublicCause } from "./PublicOrganizerService";
import type { AnswerAddressInput } from "./addressAnswer";

// ── Read (form) shapes ────────────────────────────────────────────────────────

export interface PublicTicketType {
  ticketTypeId: string;
  name?: string | null;
  description?: string | null;
  isAddOn?: boolean | null;
  addonCategoryId?: string | null;
  requiresTicket?: boolean | null;
  isPayWhatYouWant?: boolean | null;
  price?: number | null;
  /** The current price — a member's price when the form was read with a valid membership number. */
  effectivePrice?: number | null;
  /**
   * #162 — a price that depends on a count: what 1, 2, … of them cost TOGETHER now (`costs[n − 1]` = n tickets — the tier
   * ladder by tickets sold, and "buy more, pay less" by tickets in the order); how many more sell at the current price and
   * the price after; the "buy more" offers. Null / empty otherwise.
   */
  costs?: number[] | null;
  atThisPrice?: number | null;
  nextPrice?: number | null;
  quantityDeals?: PublicTicketQuantityDeal[] | null;
  minPrice?: number | null;
  maxPrice?: number | null;
  currencyCode?: string | null;
  /** any | optional | required — `required` = members only. */
  membershipMode?: string | null;
  /** The membership types it accepts, by name (empty = any membership). */
  membershipTypeNames?: string[] | null;
  /** It has member prices. */
  hasMemberPrices?: boolean | null;
  minPerOrder?: number | null;
  maxOrderCount?: number | null;
  /** "Sold in multiples of": a count of this type in one order is a multiple of it. 1 = any count (always, for pay what you want). */
  orderIncrement?: number | null;
  quantityTotal?: number | null;
  /** This type's OWN stock left (null = unlimited) — the room every admission type shares is the form's `placesLeft`. */
  available?: number | null;
  /** Nothing of it can be bought now — its stock is gone, or (an admission) the room is full. */
  soldOut?: boolean | null;
  /**
   * ROADMAP #36: this type is sold out AND the host runs a waitlist, so the row offers "Join the
   * waitlist" instead of a dead Sold out badge. Add-ons and closed sales windows are never
   * waitlistable — an add-on is bought alongside a ticket, never instead of one.
   */
  waitlistable?: boolean | null;
  salesMode?: string | null;
  salesStartsAt?: string | null;
  salesEndsAt?: string | null;
  onSale?: boolean | null;
  orderNo?: number | null;
  /** ROADMAP A4: the one date this type is sold for, when the host restricted it (null = any date). */
  eventTimeSlotId?: string | null;
  /**
   * ROADMAP A4: on a recurring event, this type on each bookable date (null otherwise). The type-level
   * `available` is null there, and `soldOut` / `onSale` summarise these.
   */
  dates?: PublicTicketTypeDate[] | null;
}

/** ROADMAP A4: one date of a recurring event a buyer can choose. */
export interface PublicTicketDate {
  eventTimeSlotId: string;
  name?: string | null;
  /** Its name, else its start in the event's timezone — ready to show. */
  label?: string | null;
  startsAt?: string | null;
  endsAt?: string | null;
  isAllDay?: boolean | null;
  /** People this date holds; null = uncapped. */
  capacity?: number | null;
  /** Places left on this date; null = uncapped. */
  remaining?: number | null;
  soldOut?: boolean | null;
}

/** #162 d — a "buy more, pay less" offer: from `minQuantity` tickets in one order, each gets the effect (amount signed). */
export interface PublicTicketQuantityDeal {
  minQuantity?: number | null;
  maxQuantity?: number | null;
  effectKind?: string | null;
  amount?: number | null;
}

/** ROADMAP A4: a ticket type on one date of a recurring event. */
export interface PublicTicketTypeDate {
  eventTimeSlotId: string;
  available?: number | null;
  soldOut?: boolean | null;
  onSale?: boolean | null;
  effectivePrice?: number | null;
  /**
   * #162 — a price that depends on a count: what 1, 2, … of them cost TOGETHER now (`costs[n − 1]` = n tickets — the tier
   * ladder by tickets sold, and "buy more, pay less" by tickets in the order); how many more sell at the current price and
   * the price after; the "buy more" offers. Null / empty otherwise.
   */
  costs?: number[] | null;
  atThisPrice?: number | null;
  nextPrice?: number | null;
  quantityDeals?: PublicTicketQuantityDeal[] | null;
}

export interface PublicTicketAddonCategory {
  id: string;
  name?: string | null;
  description?: string | null;
  orderNo?: number | null;
}

/** What a membership number given to the form is worth on this event. */
export interface PublicTicketMembership {
  valid?: boolean | null;
  /** Why it cannot be used here (null when valid). */
  error?: string | null;
  typeName?: string | null;
  /** Uses left on a pass with a limit (null = unlimited). */
  usesLeft?: number | null;
}

export interface PublicTicketForm {
  event?: PublicRsvpEventRef | null;
  /** The form asks for a membership number (members-only types, or member prices). */
  asksMembership?: boolean | null;
  /** The number the form was read with, checked (null when none was given). */
  membership?: PublicTicketMembership | null;
  eventMode?: string | null;
  guestTracking?: string | null;
  isTestMode?: boolean | null;
  serviceFeePayer?: string | null;
  /** absorb | service | service_processor — how ticket fees are passed to the guest (R14). */
  feeMode?: string | null;
  minOrderAmount?: number | null;
  /** #77 — the most admission tickets one order may hold, per date on a recurring event (null = no limit). */
  maxTicketsPerOrder?: number | null;
  /**
   * #37 phase 2 — the host reviews applicants, so checkout AUTHORIZES the card rather than
   * charging it. The buyer must be told before they enter a card.
   */
  needsApproval?: boolean | null;
  /** Non-refundable application fee kept per person if they are not accepted (null = nothing). */
  approvalDepositAmount?: number | null;
  /** Host-configured noun for admission tickets (default "Tickets"). */
  ticketLabel?: string | null;
  /** Host-configured noun for add-ons (default "Add-ons"). */
  addonLabel?: string | null;
  /** A2e #87 — checkout asks (and the server requires) the buyer's postal address. On unless the host turned it off. */
  askBuyerAddress?: boolean | null;
  accepting?: boolean | null;
  message?: string | null;
  currencyCode?: string | null;
  paymentReady?: boolean | null;
  /**
   * #204 step 5 — the event's causes a completed purchase may add a gift to (its OWN order, after the tickets — the
   * account-order rail); empty when gifts cannot be taken now. ⓘ An API older than this answers without it.
   */
  giftCauses?: PublicCause[] | null;
  /** #204 step 5 — those gifts take TEST cards (the event is in test mode). */
  giftsTestMode?: boolean | null;
  ticketTypes?: PublicTicketType[] | null;
  /** single | recurring | agenda. */
  scheduleMode?: string | null;
  /** ROADMAP A4: on a recurring event, the dates still bookable (null otherwise). Every cart item then names one. */
  dates?: PublicTicketDate[] | null;
  /** The event's free places, shared by every admission type (2026-10-23); null = uncapped, or a recurring event (each
   *  date carries its own `remaining`). */
  placesLeft?: number | null;
  addonCategories?: PublicTicketAddonCategory[] | null;
  questions?: PublicRsvpFormQuestion[] | null;
  /**
   * Waivers / consents each ATTENDEE must accept — the same rows, shape and rules as the RSVP form:
   * an attendee owes the same policies however they registered. Age-gated ones need a birthdate,
   * which only exists as a custom question mapped to `personField: 'birthday'`.
   */
  policies?: PublicRsvpFormPolicy[] | null;
  /** The event's age of majority (default 18) — see PublicRsvpForm.ageOfMajority. */
  ageOfMajority?: number | null;
  walletAppleUrl?: string | null;
  walletGoogleUrl?: string | null;
  /** ROADMAP #36: the host runs a waitlist, so a sold-out ticket offers to queue. */
  enableWaitlist?: boolean | null;
  /** ROADMAP #36: every ADMISSION is gone — the drawer leads with the waitlist. */
  waitlisting?: boolean | null;
  /**
   * ROADMAP #36: the waitlist claimant this checkout is for, when a valid `waitlistToken` was
   * supplied. Prefill their details rather than asking again — they gave us exactly this when they
   * joined the queue, and a re-typed variant of their email mints a SECOND person, which duplicates
   * them on the host's guest list.
   */
  claimGuest?: { firstName?: string | null; lastName?: string | null; email?: string | null; phone?: string | null } | null;
}

// ── Write shapes ──────────────────────────────────────────────────────────────

export interface PublicTicketItemInput {
  ticketTypeId: string;
  quantity: number;
  /** Buyer-named price for pay-what-you-want types. */
  unitPrice?: number | null;
  /** ROADMAP A4: the date this line is for (a recurring event). A type may appear once per date. */
  eventTimeSlotId?: string | null;
}

export interface PublicTicketReserveInput {
  url: string;
  eventTimeSlotId?: string | null;
  reservationMinutes?: number | null;
  items: PublicTicketItemInput[];
  discountCode?: string | null;
  /** The buyer's membership number — unlocks members-only tickets and member prices (checked on the server). */
  membershipNumber?: string | null;
  /**
   * ROADMAP #36: a waitlist claim token. The places held for this guest are given back to them for
   * this checkout only; everyone else still sees them as taken. A bad, expired or already-claimed
   * token is ignored rather than rejected, so a stale link degrades to an ordinary purchase.
   */
  waitlistToken?: string | null;
  /**
   * R23: the hold this reserve REPLACES, when the guest changed their cart. The server releases it
   * inside the SAME transaction, before taking the new hold, so the seats being dropped are
   * countable again by this very reserve. Best-effort on the server — a hold it cannot release
   * (one already at the card step, say) is left to the sweeper and never fails the reserve.
   */
  replaceOrderId?: string | null;
  /**
   * A2b #59 — the `?ch=` marker that FIRST introduced this browser to the event (first-touch
   * attribution, decided in the browser by `lib/attribution.ts`). Null when the browser never
   * arrived through a tagged link; the report shows that as "Direct / untagged". A marker that
   * matches no channel is simply not credited.
   */
  marketingChannelCode?: string | null;
}

/** ROADMAP #36 — join the waitlist for a sold-out ticketed event. */
export interface PublicTicketWaitlistJoinInput {
  url: string;
  /** Which sold-out type they want. Omit for the event-wide queue (a capacity-capped event). */
  ticketTypeId?: string | null;
  quantity?: number | null;
  guest: {
    firstName?: string | null;
    lastName?: string | null;
    /** Required — the whole waitlist depends on reaching them later with an offer. */
    email: string;
    phone?: string | null;
  };
  visitorId?: string | null;
}

export interface PublicTicketWaitlistJoinOutcome {
  joined?: boolean | null;
  eventGuestId?: string | null;
  partySize?: number | null;
  ticketTypeId?: string | null;
  ticketTypeName?: string | null;
}

export interface PublicTicketBuyerInput {
  firstName?: string | null;
  lastName?: string | null;
  name?: string | null;
  email?: string | null;
  phone?: string | null;
  answers?: PublicRsvpAnswerInput[] | null;
  /**
   * HEADCOUNT tracking: the buyer is the single guest of record for the order (the admissions under
   * them are not people), so they sign the waivers once and the server records the acceptances
   * against their guest row. In names tracking each attendee carries their own instead.
   */
  policyAcceptances?: PublicRsvpPolicyAcceptanceInput[] | null;
  /** The adult who signs the buyer's waivers when the buyer is a minor (headcount tracking). */
  guardian?: PublicRsvpGuardianInput | null;
  /** A2e #87 — the buyer's postal address (`toAddressInput`); required when the form's `askBuyerAddress` is on. */
  address?: AnswerAddressInput | null;
}

export interface PublicTicketGuestInput {
  ticketId: string;
  firstName?: string | null;
  lastName?: string | null;
  name?: string | null;
  email?: string | null;
  phone?: string | null;
  /** Per-order attendee id — tickets sharing it map to ONE person (no duplicate persons). */
  attendeeKey?: string | null;
  answers?: PublicRsvpAnswerInput[] | null;
  /** This attendee's waiver / consent acceptances — one entry per policy that applies to them. */
  policyAcceptances?: PublicRsvpPolicyAcceptanceInput[] | null;
  /** The adult who signs this attendee's waivers when they are a minor. */
  guardian?: PublicRsvpGuardianInput | null;
}

export interface PublicTicketPayInput {
  url: string;
  orderId: string;
  buyer?: PublicTicketBuyerInput | null;
  buyerIsAttendee?: boolean | null;
  guests?: PublicTicketGuestInput[] | null;
  groupAnswers?: PublicRsvpAnswerInput[] | null;
  /** The browser's visitor id — binds the tickets to this device for restore / wallet. */
  visitorId?: string | null;
  note?: string | null;
}

// ── "My tickets" (restore) shapes ──────────────────────────────────────────────

export interface PublicTicketMineTicket {
  id: string;
  ticketCode?: string | null;
  ticketTypeId?: string | null;
  ticketTypeName?: string | null;
  /** ROADMAP A4: the date this ticket admits to (a recurring event); null otherwise. */
  eventTimeSlotId?: string | null;
  /** ROADMAP A4: how to show that date. */
  dateLabel?: string | null;
  /** ROADMAP A4: that date's start (ISO UTC) — for grouping by day. */
  dateStartsAt?: string | null;
  /** #199: the organizer cancelled this ticket's date (the ticket stays unless refunded, and says so). */
  dateCanceled?: boolean | null;
  /** #199: the organizer's reason, when they gave one. */
  dateCancelReason?: string | null;
  isAddOn?: boolean | null;
  addonCategoryId?: string | null;
  addonCategoryName?: string | null;
  ticketState?: string | null;
  holderName?: string | null;
  holderGuestCode?: string | null;
  orderId?: string | null;
  price?: number | null;
  currencyCode?: string | null;
  isHolder?: boolean | null;
  isBuyer?: boolean | null;
  canTransfer?: boolean | null;
  /** The buyer has GIVEN this ticket away — the buyer's view shows "Transferred" with no QR. */
  isTransferred?: boolean | null;
  /** Signed token to this order's printable receipt (R19). Only set for tickets the visitor BOUGHT. */
  receiptToken?: string | null;
  /** #40 — a place restored after a refusal, still unpaid: what is owed for it. Null on a settled ticket. */
  paymentDueAmount?: number | null;
  /** #40 — signed token to the payment-only checkout for that restored place. Set for the HOLDER too, not only the buyer. */
  payToken?: string | null;
  /**
   * #37 phase 2 — set only when the holder has no place yet: 'pending' (awaiting the host's
   * review) or 'denied'. `ticketCode` is withheld by the server in that case, so there is no
   * QR to render — show the reason instead.
   */
  holderStatus?: string | null;
  /**
   * The host's note on a refusal — present ONLY when they chose to share it. An internal crew
   * note never reaches the guest.
   */
  holderDecisionReason?: string | null;
}

/** Who the buyer is giving a ticket to (an email OR phone is required). */
export interface PublicTicketTransferRecipientInput {
  firstName?: string | null;
  lastName?: string | null;
  name?: string | null;
  email?: string | null;
  phone?: string | null;
}

export interface PublicTicketMine {
  event?: PublicRsvpEventRef | null;
  found?: boolean | null;
  ticketLabel?: string | null;
  addonLabel?: string | null;
  tickets?: PublicTicketMineTicket[] | null;
  /** Waiver status for every guest on the visitor's orders. See PublicTicketMineWaiverGuest. */
  waiverGuests?: PublicTicketMineWaiverGuest[] | null;
  walletAppleUrl?: string | null;
  walletGoogleUrl?: string | null;
}

/** One waiver belonging to a guest on the visitor's orders. */
export interface PublicTicketMineWaiver {
  eventPolicyId?: string | null;
  policyName?: string | null;
  isRequired?: boolean | null;
  signed?: boolean | null;
  signedAt?: string | null;
  /** ⚠ For a minor's waiver this is the PARENT / GUARDIAN, not the guest it is about. */
  signerName?: string | null;
  /** self | guardian. */
  acceptedByRole?: string | null;
  forMinor?: boolean | null;
  /** Masked destination of the signing link — who still has to act. */
  sentTo?: string | null;
  /**
   * The document's own page — to SIGN it when unsigned, or to READ and PRINT it once signed.
   *
   * ⚠ Present ONLY for a waiver THIS visitor may open (their own place, or one addressed to their
   * own contact). Everyone else's is status-only — holding the tickets does not make you the other
   * people on the order.
   */
  documentUrl?: string | null;
  /**
   * ROADMAP A2.7.1 — this visitor MIGHT be the person the waiver is for. `documentUrl` stays null;
   * `waiverRequestCode` sends a code to the waiver's own address and `waiverVerifyCode` returns the
   * link after a correct one. False once too many wrong codes were tried.
   */
  canVerify?: boolean | null;
}

export interface PublicTicketMineWaiverGuest {
  eventGuestId?: string | null;
  guestName?: string | null;
  waivers?: PublicTicketMineWaiver[] | null;
}

/** Which waiver a code is about. Whether this browser may verify it is re-derived on the server. */
export interface PublicTicketWaiverCodeInput {
  url: string;
  visitorId: string | null;
  eventGuestId: string | null;
  eventPolicyId: string | null;
  /** The 6-digit code — verify only. */
  code?: string | null;
}

export interface PublicTicketWaiverCode {
  /** Request: a code is on its way. */
  sent?: boolean | null;
  /** Request: where it went, masked. */
  sentTo?: string | null;
  /** Verify: the waiver's own page — returned only after a correct code. */
  documentUrl?: string | null;
}

// ── Receipt shapes (R19) ────────────────────────────────────────────────────────

export interface PublicTicketReceiptLine {
  ticketTypeId?: string | null;
  name?: string | null;
  isAddOn?: boolean | null;
  addonCategoryName?: string | null;
  /** ROADMAP A4: the date this line's tickets admit to (a recurring event); null otherwise. */
  eventTimeSlotId?: string | null;
  dateLabel?: string | null;
  dateStartsAt?: string | null;
  /** #199: this line's date was cancelled after the purchase. */
  dateCanceled?: boolean | null;
  quantity?: number | null;
  unitPrice?: number | null;
  lineTotal?: number | null;
}

export interface PublicTicketReceiptPricing {
  subtotal?: number | null;
  discount?: number | null;
  tax?: number | null;
  /**
   * The part of "tax" that was ADDED to reach the total (tax-EXCLUSIVE lines only).
   * ⚠⚠ A breakdown reads as a running sum, and an INCLUSIVE rate is already inside the subtotal —
   * so rendering "tax" as a summed row makes the column wrong ("Subtotal 100, Tax 16.67, Total
   * 100"). Sum this one; state (tax − taxAdded) separately as "includes X tax".
   */
  taxAdded?: number | null;
  serviceFee?: number | null;
  processorFee?: number | null;
  total?: number | null;
}

/** The payment-only checkout a re-approved applicant is emailed (#40). */
export interface PublicTicketPayLink {
  found?: boolean | null;
  /** Already settled — show the receipt route, never a second card field. */
  paid?: boolean | null;
  /** #150 — paid AFTER the place was released: no ticket, the payment refunded in full. Absent on an older gateway. */
  refunded?: boolean | null;
  /** The hold behind the link lapsed (or was released), so the seat is gone. */
  /**
   * #40. The HOST withdrew the restored place before it was paid for. Deliberately distinct from
   * `expired`: the time did not run out, they changed their mind — so the page must not suggest
   * asking for a fresh link.
   */
  canceled?: boolean | null;
  expired?: boolean | null;
  orderId?: string | null;
  eventTitle?: string | null;
  eventSlug?: string | null;
  eventStartsAt?: string | null;
  eventTimezone?: string | null;
  hostedBy?: string | null;
  holderName?: string | null;
  ticketName?: string | null;
  amountDue?: number | null;
  /** The full price of the place, before the application-fee credit. */
  originalAmount?: number | null;
  /** The non-refundable application fee already paid, credited against the place. */
  feeCredit?: number | null;
  currencyCode?: string | null;
  expiresAt?: string | null;
  isTest?: boolean | null;
  /**
   * The event's website theme, so this standalone page renders in the host's own look.
   *
   * ⚠ It arrives on THIS token-authorized read on purpose — the page has no visitorId and no
   * login, so the theme cannot be fetched by slug on the client without opening a second,
   * unauthorized path to it. Null when the event has no theme; the page then renders its default.
   */
  theme?: PublicEventTheme | null;
}

export interface PublicTicketReceipt {
  eventTitle?: string | null;
  eventSlug?: string | null;
  eventStartsAt?: string | null;
  eventTimezone?: string | null;
  hostedBy?: string | null;
  reference?: string | null;
  paidAt?: string | null;
  currencyCode?: string | null;
  /** paid | refunded | partially_refunded. */
  status?: string | null;
  isTest?: boolean | null;
  buyerName?: string | null;
  buyerEmail?: string | null;
  /** A2e #87 — where the order was billed, as envelope lines ([] when none). */
  buyerAddressLines?: string[] | null;
  lineItems?: PublicTicketReceiptLine[] | null;
  pricing?: PublicTicketReceiptPricing | null;
  refundedAmount?: number | null;
  /**
   * The event's website theme, so this standalone page renders in the host's own look.
   *
   * ⚠ It arrives on THIS token-authorized read on purpose — the page has no visitorId and no
   * login, so the theme cannot be fetched by slug on the client without opening a second,
   * unauthorized path to it. Null when the event has no theme; the page then renders its default.
   */
  theme?: PublicEventTheme | null;
}

export interface PublicTicketReceiptEnvelope {
  found?: boolean | null;
  receipt?: PublicTicketReceipt | null;
}

// ── Result shapes ─────────────────────────────────────────────────────────────

export interface PublicTicketOrderTicket {
  id: string;
  ticketTypeId?: string | null;
  /** ROADMAP A4: the date this held ticket admits to (a recurring event). */
  eventTimeSlotId?: string | null;
  ticketState?: string | null;
  price?: string | null;
  ticketCode?: string | null;
  holderName?: string | null;
}

export interface PublicTicketOrderPricing {
  subtotal?: number | null;
  discount?: number | null;
  tax?: number | null;
  /**
   * The part of "tax" that was ADDED to reach the total (tax-EXCLUSIVE lines only).
   * ⚠⚠ A breakdown reads as a running sum, and an INCLUSIVE rate is already inside the subtotal —
   * so rendering "tax" as a summed row makes the column wrong ("Subtotal 100, Tax 16.67, Total
   * 100"). Sum this one; state (tax − taxAdded) separately as "includes X tax".
   */
  taxAdded?: number | null;
  /** Ticket amount (net + tax) the fees are computed on. */
  baseTotal?: number | null;
  /** Our service fee (informational). */
  serviceFee?: number | null;
  /** Estimated card-processing fee (informational). */
  processorFee?: number | null;
  /** Our service fee added to the guest's total (0 under 'absorb'). */
  serviceFeeToGuest?: number | null;
  /** Estimated card fee added to the guest's total (only 'service_processor'). */
  processorFeeToGuest?: number | null;
  /** Estimated host proceeds. */
  hostNet?: number | null;
  /** Resolved fee mode. */
  feeMode?: string | null;
  /** Legacy aliases. */
  fee?: number | null;
  feeToGuest?: number | null;
  total?: number | null;
  currencyCode?: string | null;
}

export interface PublicTicketOrder {
  id: string;
  eventId?: string | null;
  orderStatus?: string | null;
  reservedUntil?: string | null;
  subtotalAmount?: string | null;
  totalAmount?: string | null;
  currencyCode?: string | null;
  quantity?: number | null;
  tickets?: PublicTicketOrderTicket[] | null;
  pricing?: PublicTicketOrderPricing | null;
}

export interface PublicTicketPaymentIntent {
  orderId?: string | null;
  paymentIntentId?: string | null;
  clientSecret?: string | null;
  connectedAccountId?: string | null;
  publishableKey?: string | null;
  amount?: number | null;
  amountMinor?: number | null;
  currencyCode?: string | null;
}

/**
 * The pay answer for an order that costs NOTHING (free tickets, or a discount covering all of it): the server issued it
 * there and then — no card, no Stripe — and says so with no client secret and an amount of 0. The site finishes it as
 * it does after a card (`finalize` returns the issued order). Any other answer without a client secret is a failure.
 */
export const isNothingToPay = (handoff: PublicTicketPaymentIntent | null | undefined): boolean =>
  !!handoff?.orderId && !handoff.clientSecret && handoff.amount != null && Number(handoff.amount) === 0;

// ── GraphQL documents ───────────────────────────────────────────────────────────

const PUBLIC_TICKET_FORM_QUERY = imageSafeDocs(`
  query PublicTicketForm($params: PublicTicketFormInput) {
    publicTicketForm(params: $params) {
      code
      errors { name errors }
      data {
        event { id title slug }
        eventMode
        guestTracking
        isTestMode
        serviceFeePayer
        feeMode
        minOrderAmount
        maxTicketsPerOrder
        needsApproval
        approvalDepositAmount
        ticketLabel
        addonLabel
        askBuyerAddress
        accepting
        message
        currencyCode
        paymentReady
        giftCauses { ${PUBLIC_CAUSE_FIELD_LIST} }
        giftsTestMode
        asksMembership
        membership { valid error typeName usesLeft }
        ticketTypes {
          ticketTypeId name description isAddOn addonCategoryId requiresTicket
          isPayWhatYouWant price effectivePrice costs atThisPrice nextPrice quantityDeals { minQuantity maxQuantity effectKind amount } minPrice maxPrice currencyCode
          membershipMode membershipTypeNames hasMemberPrices
          minPerOrder maxOrderCount orderIncrement quantityTotal available soldOut waitlistable
          salesMode salesStartsAt salesEndsAt onSale orderNo
          eventTimeSlotId
          dates { eventTimeSlotId available soldOut onSale effectivePrice costs atThisPrice nextPrice quantityDeals { minQuantity maxQuantity effectKind amount } }
        }
        scheduleMode
        dates { eventTimeSlotId name label startsAt endsAt isAllDay capacity remaining soldOut }
        placesLeft
        addonCategories { id name description orderNo }
        questions {
          id questionType valueType title helpText placeholder isRequired requiredScope
          personField ${QUESTION_IMAGE_FIELDS} askOncePerGroup conditionMatch orderNo
          conditions { conditionKind operator rsvpStatuses guestGroupId }
          options { id label value price }
        }
        policies {
          id
          policyId
          name
          acceptanceLabel
          description
          body
          hasBody
          contentFormat
          isRequired
          minAge
          maxAge
          ageGated
          requireInitials
          requireSignature
          selfSign
        }
        ageOfMajority
        walletAppleUrl
        walletGoogleUrl
        enableWaitlist
        waitlisting
        claimGuest { firstName lastName email phone }
      }
    }
  }
`);

const ORDER_FIELDS = `
  id
  eventId
  orderStatus
  reservedUntil
  subtotalAmount
  totalAmount
  currencyCode
  quantity
  tickets { id ticketTypeId eventTimeSlotId ticketState price ticketCode holderName }
  pricing { subtotal discount tax taxAdded baseTotal serviceFee processorFee serviceFeeToGuest processorFeeToGuest hostNet feeMode fee feeToGuest total currencyCode }
`;

const PUBLIC_TICKET_WAITLIST_JOIN = gql`
  mutation PublicTicketWaitlistJoin($params: PublicTicketWaitlistJoinInput) {
    publicTicketWaitlistJoin(params: $params) {
      code
      errors { name errors }
      data { joined eventGuestId partySize ticketTypeId ticketTypeName }
    }
  }
`;

const PUBLIC_TICKET_RESERVE = gql`
  mutation PublicTicketReserve($params: PublicTicketReserveInput) {
    publicTicketReserve(params: $params) {
      code
      errors { name errors }
      data { ${ORDER_FIELDS} }
    }
  }
`;

// Kept as a raw STRING as well as a document: `releaseBeacon` builds its own request body (a beacon
// cannot go through the Apollo client) and needs the query text. Deriving it with graphql's `print`
// would make `graphql` a direct dependency of this package for one call — this is the same text,
// with no new dependency and nothing that can drift between the two forms.
const PUBLIC_TICKET_RELEASE_DOC = `
  mutation PublicTicketRelease($params: PublicTicketReleaseInput) {
    publicTicketRelease(params: $params) {
      code
      errors { name errors }
      data { id orderStatus }
    }
  }
`;

const PUBLIC_TICKET_RELEASE = gql(PUBLIC_TICKET_RELEASE_DOC);

/**
 * #150 — the finalize answer that means NO TICKETS: the payment arrived after the place was released, so it was refunded
 * (or its refund is still being retried). The server says so with a CONFLICT on `orderId`; this returns its explanation,
 * else null — tickets issued, or a passing hiccup the Stripe webhook finishes (those still end on the success screen).
 */
export const finalizeNotIssued = (res: OpResult | null | undefined): string | null => {
  if (res?.code !== OP_RESULT_CODES.CONFLICT) {
    return null;
  }

  return refusalReason(res, "This place was released before your payment arrived, so no ticket was issued. Please contact the organizer.");
};

const PUBLIC_TICKET_FINALIZE = gql`
  mutation PublicTicketFinalize($params: PublicTicketFinalizeInput) {
    publicTicketFinalize(params: $params) {
      code
      errors { name errors }
      data { ${ORDER_FIELDS} }
    }
  }
`;

const PUBLIC_TICKET_REQUEST_CODE = gql`
  mutation PublicTicketRequestCode($params: PublicTicketRequestCodeInput) {
    publicTicketRequestCode(params: $params) {
      code
      errors { name errors }
      data { sent }
    }
  }
`;

const PUBLIC_TICKET_REDEEM_CODE = gql`
  mutation PublicTicketRedeemCode($params: PublicTicketRedeemCodeInput) {
    publicTicketRedeemCode(params: $params) {
      code
      errors { name errors }
      data { visitorId }
    }
  }
`;

const PUBLIC_TICKET_TRANSFER_CODE = gql`
  mutation PublicTicketTransferCode($params: PublicTicketRestoreVisitorInput) {
    publicTicketTransferCode(params: $params) {
      code
      errors { name errors }
      data { code }
    }
  }
`;

const PUBLIC_TICKET_WAIVER_REQUEST_CODE = gql`
  mutation PublicTicketWaiverRequestCode($params: PublicTicketWaiverCodeInput) {
    publicTicketWaiverRequestCode(params: $params) {
      code
      errors { name errors }
      data { sent sentTo }
    }
  }
`;

const PUBLIC_TICKET_WAIVER_VERIFY_CODE = gql`
  mutation PublicTicketWaiverVerifyCode($params: PublicTicketWaiverCodeInput) {
    publicTicketWaiverVerifyCode(params: $params) {
      code
      errors { name errors }
      data { documentUrl }
    }
  }
`;

const PUBLIC_TICKETS_MINE_QUERY = gql`
  query PublicTicketsMine($params: PublicTicketMineInput) {
    publicTicketsMine(params: $params) {
      code
      errors { name errors }
      data {
        event { id title slug }
        found
        ticketLabel
        addonLabel
        walletAppleUrl
        walletGoogleUrl
        waiverGuests {
          eventGuestId
          guestName
          waivers {
            eventPolicyId policyName isRequired signed signedAt signerName acceptedByRole forMinor sentTo documentUrl canVerify
          }
        }
        tickets {
          id ticketCode ticketTypeId ticketTypeName isAddOn addonCategoryId addonCategoryName ticketState
          holderName holderGuestCode orderId price currencyCode
          isHolder isBuyer canTransfer isTransferred receiptToken holderStatus holderDecisionReason
          paymentDueAmount payToken
          eventTimeSlotId dateLabel dateStartsAt dateCanceled dateCancelReason
        }
      }
    }
  }
`;

const PUBLIC_TICKET_ORDER_RECEIPT_QUERY = gql`
  query PublicTicketOrderReceipt($params: PublicTicketReceiptInput) {
    publicTicketOrderReceipt(params: $params) {
      code
      errors { name errors }
      data {
        found
        receipt {
          eventTitle eventSlug eventStartsAt eventTimezone hostedBy
          reference paidAt currencyCode status isTest
          buyerName buyerEmail buyerAddressLines
          lineItems { ticketTypeId name isAddOn addonCategoryName eventTimeSlotId dateLabel dateStartsAt dateCanceled quantity unitPrice lineTotal }
          pricing { subtotal discount tax taxAdded serviceFee processorFee total }
          refundedAmount
          theme { id name mode tokens customCss }
        }
      }
    }
  }
`;

// #150 — `refunded` on its own line, so an older gateway that refuses it is retried without it (answerImageFields).
const PUBLIC_TICKET_PAY_LINK_DOCS = imageSafeDocs(`
  query PublicTicketPayLink($params: PublicTicketPayLinkInput) {
    publicTicketPayLink(params: $params) {
      code
      errors { name errors }
      data {
        ${PAY_LINK_REFUNDED_FIELD}
        found paid canceled expired orderId
        eventTitle eventSlug eventStartsAt eventTimezone hostedBy
        holderName ticketName
        amountDue originalAmount feeCredit currencyCode expiresAt isTest
        theme { id name mode tokens customCss }
      }
    }
  }
`);

const PUBLIC_TICKET_PAY_LINK_START = gql`
  mutation PublicTicketPayLinkStart($params: PublicTicketPayLinkInput) {
    publicTicketPayLinkStart(params: $params) {
      code
      errors { name errors }
      data {
        orderId paymentIntentId clientSecret connectedAccountId
        publishableKey amount amountMinor currencyCode
      }
    }
  }
`;

const PUBLIC_TICKET_PAY = gql`
  mutation PublicTicketPay($params: PublicTicketPayInput) {
    publicTicketPay(params: $params) {
      code
      errors { name errors }
      data {
        orderId paymentIntentId clientSecret connectedAccountId
        publishableKey amount amountMinor currencyCode
      }
    }
  }
`;

const PUBLIC_TICKET_TRANSFER = gql`
  mutation PublicTicketTransfer($params: PublicTicketTransferInput) {
    publicTicketTransfer(params: $params) {
      code
      errors { name errors }
      data { ok ticketId sentTo }
    }
  }
`;

// ── Service ─────────────────────────────────────────────────────────────────────

export class PublicTicketService {
  /**
   * Load the Buy-Tickets form for a published, ticketed event.
   *
   * Pass `waitlistToken` when the guest arrived from a waitlist offer email (#36): availability is
   * then counted the way the checkout will count it FOR THEM — the places held for them read as
   * available, everyone else's held places do not. Omitting it makes the form and the reserve gate
   * disagree, and the claimant is shown "Sold out" for the very place being held for them.
   */
  static async getForm(
    url: string,
    waitlistToken: string | null = null,
    options: Record<string, any> = {},
    /** A membership number: the form comes back priced for that member, with `membership` saying what it is worth. */
    membershipNumber: string | null = null,
  ): Promise<OpResult> {
    return executeQueryImageSafe(
      PUBLIC_TICKET_FORM_QUERY,
      { params: { url, waitlistToken, ...(membershipNumber ? { membershipNumber } : {}) } },
      { fetchPolicy: "network-only", ...options }
    );
  }

  /** Hold the selected tickets (never oversells). Returns the reserved order + tickets. */
  static async reserve(input: PublicTicketReserveInput, options: Record<string, any> = {}): Promise<OpResult> {
    return ApiService.executeMutation(PUBLIC_TICKET_RESERVE, { params: input }, options);
  }

  /**
   * ROADMAP #36 — join the waitlist for a sold-out ticketed event.
   *
   * Records an `event_guest` as waitlisted; there is no queue table. Refused (as a validation
   * error) when the ticket is actually still on sale, so a guest is never left waiting for an email
   * that will never come.
   */
  static async waitlistJoin(input: PublicTicketWaitlistJoinInput, options: Record<string, any> = {}): Promise<OpResult> {
    return ApiService.executeMutation(PUBLIC_TICKET_WAITLIST_JOIN, { params: input }, options);
  }

  /** Start the card charge for a held order — returns the Stripe client secret + connected account. */
  static async pay(input: PublicTicketPayInput, options: Record<string, any> = {}): Promise<OpResult> {
    return ApiService.executeMutation(PUBLIC_TICKET_PAY, { params: input }, options);
  }

  /**
   * R23 — hand an abandoned hold back immediately, instead of leaving the seats looking taken until
   * the server's sweeper runs. Idempotent: releasing an already-released order succeeds quietly.
   *
   * ⚠ For the DRAWER-CLOSED case use `releaseBeacon` instead — this one is a normal awaited call and
   * a page being torn down will not wait for it.
   */
  static async release(url: string, orderId: string, options: Record<string, any> = {}): Promise<OpResult> {
    return ApiService.executeMutation(PUBLIC_TICKET_RELEASE, { params: { url, orderId } }, options);
  }

  /**
   * R23 — the same release, sent so it SURVIVES the page going away (drawer closed, tab closed,
   * phone backgrounded). `navigator.sendBeacon` hands the request to the browser, which delivers it
   * after the document is gone; a normal `fetch` is cancelled mid-flight on unload, which is exactly
   * the case we most want to release.
   *
   * ⚠ Fire-and-forget by design — a beacon has NO readable response. That is why the server makes
   * this idempotent and refuses anything that isn't an abandoned online hold: nobody is listening to
   * be told otherwise. Returns whether the browser accepted it for delivery, not whether it worked.
   *
   * `endpoint` is the caller's same-origin GraphQL proxy path (events-web: `/api/public-graphql`) —
   * this package is transport-agnostic and has no endpoint of its own.
   */
  static releaseBeacon(endpoint: string, url: string, orderId: string): boolean {
    if (typeof navigator === 'undefined' || typeof navigator.sendBeacon !== 'function' || !orderId) {
      return false;
    }

    const body = JSON.stringify({
      query: PUBLIC_TICKET_RELEASE_DOC,
      variables: { params: { url, orderId } },
    });

    // A Blob carries the content-type: sendBeacon has no headers argument, and the proxy route
    // needs application/json to parse the body at all.
    return navigator.sendBeacon(endpoint, new Blob([body], { type: 'application/json' }));
  }

  /** Finalize on return: after the card is confirmed, verify the payment server-side + issue the tickets. */
  static async finalize(url: string, orderId: string, options: Record<string, any> = {}): Promise<OpResult> {
    return ApiService.executeMutation(PUBLIC_TICKET_FINALIZE, { params: { url, orderId } }, options);
  }

  /** Cross-device restore: email/SMS the buyer a 6-digit code (neutral response — no enumeration). */
  static async requestCode(url: string, contact: string, options: Record<string, any> = {}): Promise<OpResult> {
    return ApiService.executeMutation(PUBLIC_TICKET_REQUEST_CODE, { params: { url, contact } }, options);
  }

  /** Cross-device restore: redeem a code → binds this device + returns the visitorId to adopt. */
  static async redeemCode(url: string, code: string, visitorId: string | null, options: Record<string, any> = {}): Promise<OpResult> {
    return ApiService.executeMutation(PUBLIC_TICKET_REDEEM_CODE, { params: { url, code, visitorId } }, options);
  }

  /** Cross-device restore: the original browser mints a transfer code to type on another device. */
  static async transferCode(url: string, visitorId: string, options: Record<string, any> = {}): Promise<OpResult> {
    return ApiService.executeMutation(PUBLIC_TICKET_TRANSFER_CODE, { params: { url, visitorId } }, options);
  }

  /** A2.7.1: email a 6-digit code to a waiver's OWN address, so this browser can prove it is that person. */
  static async waiverRequestCode(input: PublicTicketWaiverCodeInput, options: Record<string, any> = {}): Promise<OpResult> {
    return ApiService.executeMutation(PUBLIC_TICKET_WAIVER_REQUEST_CODE, { params: input }, options);
  }

  /** A2.7.1: check that code. A correct one returns the waiver's `documentUrl`; nothing else does. */
  static async waiverVerifyCode(input: PublicTicketWaiverCodeInput, options: Record<string, any> = {}): Promise<OpResult> {
    return ApiService.executeMutation(PUBLIC_TICKET_WAIVER_VERIFY_CODE, { params: input }, options);
  }

  /** Buyer gives a ticket to a recipient (email/phone): re-points the holder + emails the recipient a claim link. */
  static async transfer(
    url: string,
    visitorId: string,
    ticketId: string,
    recipient: PublicTicketTransferRecipientInput,
    options: Record<string, any> = {}
  ): Promise<OpResult> {
    return ApiService.executeMutation(PUBLIC_TICKET_TRANSFER, { params: { url, visitorId, ticketId, recipient } }, options);
  }

  /** The visitor's own tickets for this event (restore / wallet), keyed by their browser visitorId. */
  static async mine(url: string, visitorId: string | null, options: Record<string, any> = {}): Promise<OpResult> {
    return ApiService.executeQuery(
      PUBLIC_TICKETS_MINE_QUERY,
      { params: { url, visitorId } },
      { fetchPolicy: "network-only", ...options }
    );
  }

  /** A printable itemized receipt for one paid order, authorized by a signed receipt token (R19). */
  static async receipt(url: string, token: string, options: Record<string, any> = {}): Promise<OpResult> {
    return ApiService.executeQuery(
      PUBLIC_TICKET_ORDER_RECEIPT_QUERY,
      { params: { url, token } },
      { fetchPolicy: "network-only", ...options }
    );
  }

  /**
   * The payment-only checkout a re-approved applicant is emailed (#40). No `url`: the signed token
   * already names the event and the order, so the link carries one thing and one thing only.
   */
  static async payLink(token: string, options: Record<string, any> = {}): Promise<OpResult> {
    return executeQueryImageSafe(
      PUBLIC_TICKET_PAY_LINK_DOCS,
      { params: { token } },
      { fetchPolicy: "network-only", ...options }
    );
  }

  /** Start the card charge for a pay-link order (#40) — the token is the whole input. */
  static async payLinkStart(token: string, options: Record<string, any> = {}): Promise<OpResult> {
    return ApiService.executeMutation(PUBLIC_TICKET_PAY_LINK_START, { params: { token } }, options);
  }
}
